import { EventForm } from '@components/eventCreation/CreateEventUtils';
import { TeamForm } from '@components/teamCreation/CreateTeamUtils';
import {
    Box,
    Button,
    ButtonGroup,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid2,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useFormContext, FormProvider, useForm } from 'react-hook-form';
import StripeUserPreview from './StripeUserPreview';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import useAuth from '@hooks/useAuth';
import FormInputText from '@components/forms/FormInputText';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { getCurrentUser_synchronous } from '@modules/supabase/supabase-utils';
import ButtonGroupOption from './ButtonGroupOption';
import { addUpdateEvent, verifyPayToInviteStatus } from '@modules/supabase/events';
import { toast } from 'react-toastify';
import { getUserIdFromEmail } from '@modules/supabase/users';

export default function ManagePaymentAdmin() {
    const { watch: watchParent } = useFormContext<EventForm | TeamForm>();
    const [inviteSent, setInviteSent] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [parentId, payToUserId, waiver, codeOfConduct] = watchParent([
        'id',
        'payToUserId',
        'waiver',
        'codeOfConduct',
    ]);

    const parentEntityTable = useMemo(() => {
        if (waiver) return 'event';
        else if (codeOfConduct) return 'team';
        else return 'event';
    }, [codeOfConduct, waiver]);

    const parentEntityId = parentId || '';

    if (inviteSent)
        return (
            <>
                <p>You've invited another user to setup payments</p>
                <p>This event will go LIVE once they complete payment setup</p>
                {/* Allow them to invite another email */}
                <Button
                    onClick={() => {
                        setInviteSent(false);
                    }}
                >
                    Invite another user
                </Button>
            </>
        );

    return (
        <Grid2 sx={{ width: '100%' }}>
            <Stack spacing={2} alignItems="center">
                <StripeUserPreview
                    payToUserId={payToUserId}
                    parentEntityId={parentEntityId}
                    parentEntityTable={parentEntityTable}
                />
                <Tooltip title="Do you want to invite a different email/user as the Payment Entity?">
                    <Button variant="contained" onClick={() => setShowModal(true)}>
                        Change Payment Entity <ManageAccountsIcon sx={{ marginLeft: '1rem' }} />
                    </Button>
                </Tooltip>
            </Stack>
            <EditPaymentAdminModal
                showModal={showModal}
                onClose={() => setShowModal(false)}
                parentEntityId={parentEntityId}
                parentEntityTable={parentEntityTable}
            />
        </Grid2>
    );
}

interface ModalProps {
    showModal: boolean;
    onClose: () => void;
    parentEntityId: string;
    parentEntityTable: 'event' | 'team';
}

interface EmailForm {
    email: string;
    showEmailForm: boolean;
}

const emailFormSchema: Yup.ObjectSchema<EmailForm> = Yup.object({
    email: Yup.string()
        .email()
        .required('email is required')
        .test(
            'is not current user',
            'That is your email, select "Myself" option',
            (value) => !!value && value !== getCurrentUser_synchronous()?.email,
        ),
    showEmailForm: Yup.boolean().required(),
});

const defaultValues: EmailForm = { email: '', showEmailForm: false };

function EditPaymentAdminModal({
    showModal,
    onClose,
    parentEntityId,
    parentEntityTable,
}: ModalProps) {
    const { authUser } = useAuth();
    const { getValues: getParentValues, setValue: setParentFormValue } = useFormContext<
        EventForm | TeamForm
    >();

    const formMethods = useForm<EmailForm>({
        defaultValues,
        mode: 'all',
        resolver: yupResolver(emailFormSchema),
    });
    const { reset } = formMethods;

    const onSubmitPayToUserId = useCallback(async () => {
        const parentValues = getParentValues();
        console.log('saving payToUserId', { parentValues });
        const { email, showEmailForm } = formMethods.getValues();

        const haveIdOrEmail = parentValues.payToUserId || email;

        if (!parentValues.id || !haveIdOrEmail) {
            toast.error('Error: Item not found');
            return;
        }

        toast.promise(
            addUpdateEvent(
                {
                    id: parentValues.id,
                    payToUserId: showEmailForm
                        ? await getUserIdFromEmail(email, parentEntityId, parentEntityTable)
                        : authUser?.id,
                },
                `event [onSubmitPayToUserId]`,
            )
                .then((response) => {
                    if (response) {
                        setParentFormValue('payToUserId', response.payToUserId);
                        return verifyPayToInviteStatus(
                            response.payToUserId,
                            response.id,
                            parentEntityTable,
                            email,
                        );
                    }
                    throw new Error('Error adding/adding event');
                })
                .then((response) => {
                    if (response) {
                        console.log(
                            `[onSubmitPayToUserId] event created, updating form state`,
                            response,
                        );

                        reset();
                        onClose();
                        // TODO: update the parent payment preview Stripe info
                    } else {
                        console.error('Error adding/adding event');
                    }
                }),
            { pending: 'Saving...', success: 'Saved', error: 'Error saving' },
        );
    }, [
        authUser?.id,
        formMethods,
        getParentValues,
        onClose,
        parentEntityId,
        parentEntityTable,
        reset,
        setParentFormValue,
    ]);

    return (
        <Dialog fullWidth maxWidth="sm" open={showModal} onClose={onClose}>
            <DialogTitle
                sx={{
                    marginTop: '1rem',
                    paddingBottom: 0,
                    textAlign: 'center',
                }}
            >
                Change Payment Entity
            </DialogTitle>
            <DialogContent
                sx={{
                    margin: '1rem',
                    marginBottom: '2rem',
                    display: 'flex',
                    justifyItems: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                }}
            >
                <FormProvider {...formMethods}>
                    <form>
                        <AdminEmailForm
                            onClose={onClose}
                            onSubmit={onSubmitPayToUserId}
                            parentEntityId={parentEntityId}
                            parentEntityTable={parentEntityTable}
                        />
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    );
}

function AdminEmailForm({
    onClose,
    onSubmit,
    parentEntityId,
    parentEntityTable,
}: {
    onClose: () => void;
    onSubmit: () => void;
    parentEntityId: string;
    parentEntityTable: 'event' | 'team';
}) {
    const { watch, setValue, formState } = useFormContext<EmailForm>();
    const { errors } = formState;
    const [email, showEmailForm] = watch(['email', 'showEmailForm']);
    const { authUser } = useAuth();

    return (
        <Stack>
            <ButtonGroup variant="outlined" aria-label="Basic button group">
                <ButtonGroupOption
                    onClick={() => {
                        setValue('email', authUser?.email as string);
                        setValue('showEmailForm', false);
                    }}
                    variant={showEmailForm ? 'outlined' : 'contained'}
                    selected={!showEmailForm}
                >
                    {`Myself (${authUser?.email})`}
                </ButtonGroupOption>
                <ButtonGroupOption
                    onClick={() => setValue('showEmailForm', true)}
                    selected={showEmailForm}
                    variant={showEmailForm ? 'contained' : 'outlined'}
                >
                    Other Email
                </ButtonGroupOption>
                <ButtonGroupOption
                    disabled
                    // onClick={() => setValue('email', '')}
                    selected={showEmailForm}
                    variant={showEmailForm ? 'contained' : 'outlined'}
                >
                    Existing Admin (Coming Soon)
                </ButtonGroupOption>
            </ButtonGroup>
            {showEmailForm ? (
                <Grid2 container alignItems={'center'} spacing={2} marginTop={2}>
                    <Grid2 size={{ xs: 12 }}>
                        <FormInputText name="email" label="Admin Email" preventEnterSubmission />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        {email ? null : (
                            <Typography sx={{ marginTop: '1rem', textAlign: 'center' }}>
                                Enter the email for the payment entity
                            </Typography>
                        )}
                    </Grid2>
                </Grid2>
            ) : null}
            <StripeUserPreview
                email={showEmailForm ? email : authUser?.email}
                parentEntityId={parentEntityId}
                parentEntityTable={parentEntityTable}
            />
            <Box display="flex" justifyContent="space-around" marginTop="2rem">
                <Button variant="outlined" color="error" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    disabled={showEmailForm && !!errors.email}
                    variant="contained"
                    onClick={onSubmit}
                >
                    Save
                </Button>
            </Box>
        </Stack>
    );
}
